import React from 'react';
import { css } from '@emotion/react';

const headerStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 96px;
  padding: 0 var(--grn-space-xl2);
  background: var(--grn-color-background);
  box-shadow: 0px 4px 15px 0px rgba(0, 0, 0, 0.02);
  position: sticky;
  top: 0;
  z-index: 10;
`;

const logoContainerStyles = css`
  width: 280px;
  display: flex;
  align-items: center;
`;

const logoStyles = css`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const navStyles = css`
  display: flex;
  align-items: center;
  gap: var(--grn-space-l);
`;

const navItemStyles = css`
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: var(--grn-text-m);
  line-height: 20px;
  color: var(--grn-color-content2);
  cursor: pointer;
  text-decoration: none;

  &:hover {
    color: var(--grn-color-content);
  }
`;

const navItemActiveStyles = css`
  color: var(--grn-color-content);
`;

const accountStyles = css`
  width: 280px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const avatarStyles = css`
  width: 40px;
  height: 40px;
  border-radius: 100px;
  border: 1px solid #c9c9c9;
  background: var(--grn-color-shade3);
  overflow: hidden;
`;

const chevronStyles = css`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--grn-color-content2);
`;

type HeaderProps = {
  slowMode: boolean;
  onToggleSlowMode: () => void;
};

export default function Header({ slowMode, onToggleSlowMode }: HeaderProps) {
  return (
    <header css={headerStyles}>
      <div css={logoContainerStyles}>
        <div
          css={logoStyles}
          onClick={onToggleSlowMode}
          role="button"
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === 'Enter') onToggleSlowMode();
          }}
        >
          {slowMode ? (
            <svg
              width="78"
              height="13"
              viewBox="0 0 54 9"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_3105_14076)">
                <path
                  d="M4.908 4.068L3.552 4.116C3.444 3.648 3.072 3.312 2.472 3.312C1.884 3.312 1.536 3.6 1.536 4.008C1.536 4.344 1.74 4.5 2.244 4.668L3.456 5.088C4.464 5.436 5.052 5.844 5.052 6.804C5.052 8.112 3.984 8.82 2.544 8.82C1.092 8.82 0.096 8.112 0 6.864L1.428 6.804C1.464 7.272 1.848 7.704 2.556 7.704C3.216 7.704 3.576 7.392 3.576 6.96C3.576 6.54 3.3 6.396 2.628 6.168L1.74 5.88C0.72 5.556 0.096 5.064 0.096 4.128C0.096 2.952 1.08 2.172 2.544 2.172C3.876 2.172 4.812 2.904 4.908 4.068ZM6.32653 8.664V0H7.76653V8.664H6.32653ZM12.3951 2.196C14.3271 2.196 15.7311 3.612 15.7311 5.52C15.7311 7.428 14.3271 8.832 12.3951 8.832C10.4391 8.832 9.07115 7.428 9.07115 5.52C9.07115 3.612 10.4391 2.196 12.3951 2.196ZM12.3951 3.468C11.2431 3.468 10.4991 4.344 10.4991 5.508C10.4991 6.708 11.2431 7.572 12.3951 7.572C13.5591 7.572 14.2791 6.696 14.2791 5.508C14.2791 4.356 13.5471 3.468 12.3951 3.468ZM18.2528 8.664L16.2128 2.352H17.7248L18.5408 5.364C18.7088 5.964 18.8528 6.564 18.9848 7.152H19.0208C19.1528 6.564 19.2968 5.976 19.4528 5.376L20.2208 2.352H21.6968L22.4888 5.4C22.6448 6.024 22.7648 6.492 22.9088 7.116H22.9448C23.0888 6.492 23.2088 6.024 23.3768 5.4L24.1928 2.352H25.6448L23.6168 8.664H22.1768L20.9768 3.9H20.9408C20.7968 4.512 20.6888 4.992 20.5208 5.616L19.6928 8.664H18.2528ZM32.6614 0V8.664H31.3294V8.208C31.3294 8.04 31.3294 7.908 31.3534 7.8H31.3054C30.8734 8.436 30.1054 8.808 29.1934 8.808C27.3934 8.808 26.1574 7.416 26.1574 5.532C26.1574 3.648 27.3934 2.208 29.1334 2.208C30.0574 2.208 30.8254 2.604 31.2334 3.18H31.2694C31.2574 2.52 31.2454 2.352 31.2454 1.608V0H32.6614ZM29.4574 3.492C28.3054 3.492 27.5854 4.404 27.5854 5.52C27.5854 6.66 28.3174 7.56 29.4574 7.56C30.5854 7.56 31.3294 6.672 31.3294 5.508C31.3294 4.368 30.5854 3.492 29.4574 3.492ZM38.9415 6.708L40.3335 6.96C39.9375 8.16 38.6175 8.82 37.2615 8.82C35.3175 8.82 33.9735 7.452 33.9735 5.52C33.9735 3.636 35.3295 2.196 37.2495 2.196C39.3255 2.196 40.4535 3.72 40.4535 5.58C40.4535 5.664 40.4535 5.82 40.4415 5.94H35.4015C35.4495 6.984 36.3135 7.62 37.3215 7.62C38.0055 7.62 38.5815 7.308 38.9415 6.708ZM35.4255 4.848H38.9655C38.8335 3.924 38.2215 3.372 37.2495 3.372C36.2655 3.372 35.5695 3.984 35.4255 4.848ZM46.2283 4.068L44.8723 4.116C44.7643 3.648 44.3923 3.312 43.7923 3.312C43.2043 3.312 42.8563 3.6 42.8563 4.008C42.8563 4.344 43.0603 4.5 43.5643 4.668L44.7763 5.088C45.7843 5.436 46.3723 5.844 46.3723 6.804C46.3723 8.112 45.3043 8.82 43.8643 8.82C42.4123 8.82 41.4163 8.112 41.3203 6.864L42.7483 6.804C42.7843 7.272 43.1683 7.704 43.8763 7.704C44.5363 7.704 44.8963 7.392 44.8963 6.96C44.8963 6.54 44.6203 6.396 43.9483 6.168L43.0603 5.88C42.0403 5.556 41.4163 5.064 41.4163 4.128C41.4163 2.952 42.4003 2.172 43.8643 2.172C45.1963 2.172 46.1323 2.904 46.2283 4.068ZM47.6468 8.664V0H49.0508V1.944C49.0508 3 49.0268 3.66 49.0148 4.596L49.0268 4.608C49.4708 4.128 49.8788 3.768 50.3348 3.3L51.2588 2.352H53.2148L49.8668 5.328L53.3228 8.664H51.2948C50.5028 7.872 49.7348 7.044 49.0388 6.264H49.0268C49.0388 6.792 49.0508 7.224 49.0508 7.824V8.664H47.6468Z"
                  fill="black"
                />
              </g>
              <defs>
                <clipPath id="clip0_3105_14076">
                  <rect width="54" height="9" fill="white" />
                </clipPath>
              </defs>
            </svg>
          ) : (
            <svg
              width="62"
              height="13"
              viewBox="0 0 62 13"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M15.5984 3.272C18.4521 3.272 20.5259 5.34776 20.5259 8.14473C20.5259 10.9418 18.4521 13 15.5984 13C12.7093 13 10.6887 10.9418 10.6886 8.14473C10.6886 5.34776 12.7093 3.272 15.5984 3.272ZM15.5984 5.13666C13.8968 5.13666 12.7979 6.42087 12.7979 8.12717C12.7979 9.88633 13.8968 11.1529 15.5984 11.1529C17.3177 11.1529 18.3812 9.86865 18.3812 8.12717C18.3812 6.43844 17.3 5.13666 15.5984 5.13666Z"
                fill="#111110"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M38.2684 3.272C41.3348 3.272 43.001 5.5061 43.001 8.23274C43.001 8.35586 43.001 8.58457 42.9833 8.7605H35.5389C35.6098 10.2909 36.8859 11.2232 38.3748 11.2233C39.3851 11.2233 40.236 10.7658 40.7677 9.88635L42.8238 10.2558C42.2389 12.0149 40.2891 12.9823 38.2862 12.9823C35.4148 12.9823 33.4296 10.9769 33.4296 8.14473C33.4297 5.38298 35.4325 3.27202 38.2684 3.272ZM38.2684 4.99596C36.8151 4.99598 35.787 5.89323 35.5743 7.15977H40.8031C40.6082 5.80522 39.7042 4.99596 38.2684 4.99596Z"
                fill="#111110"
              />
              <path
                d="M48.0347 3.23678C50.0021 3.23678 51.3847 4.30981 51.5265 6.01615L49.5236 6.0866C49.3641 5.40048 48.8145 4.90795 47.9283 4.90795C47.0599 4.90796 46.5458 5.33011 46.5458 5.92814C46.5458 6.42075 46.8471 6.64943 47.5916 6.89574L49.3817 7.51141C50.8706 8.02159 51.7392 8.61965 51.7392 10.0269C51.7392 11.9444 50.1616 12.9822 48.0347 12.9822C45.89 12.9822 44.4188 11.9444 44.277 10.1149L46.3863 10.0269C46.4395 10.713 47.0066 11.3463 48.0524 11.3463C49.0273 11.3463 49.5589 10.889 49.5589 10.2557C49.5589 9.63998 49.1513 9.42885 48.1588 9.09467L46.8471 8.67239C45.3406 8.19746 44.4188 7.47624 44.4188 6.10416C44.4188 4.38024 45.8722 3.23678 48.0347 3.23678Z"
                fill="#111110"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M31.4973 12.7531H29.5298V12.0846C29.5298 11.8383 29.5299 11.6448 29.5653 11.4865H29.4944C28.8562 12.4188 27.7219 12.9642 26.3748 12.9642C23.7161 12.9642 21.8904 10.9236 21.8904 8.16179C21.8904 5.39996 23.7161 3.28899 26.2861 3.28896C27.651 3.28896 28.7854 3.86954 29.388 4.71397H29.4412C29.4235 3.74642 29.4058 3.50013 29.4058 2.40947V0.0521834H31.4973V12.7531ZM26.7647 5.17128C25.0632 5.17129 23.9997 6.50822 23.9997 8.14413C23.9997 9.81535 25.0809 11.1346 26.7647 11.1346C28.4309 11.1346 29.5298 9.83293 29.5298 8.12657C29.5298 6.4554 28.4309 5.17128 26.7647 5.17128Z"
                fill="#111110"
              />
              <path
                d="M4.3957 0C4.59072 0 4.7857 4.35338e-05 5.01611 0.0352238V1.84709C4.94522 1.82948 4.89197 1.82953 4.80339 1.82953C4.02355 1.82954 3.50955 2.09338 3.40324 2.83216C3.38548 2.99053 3.38548 3.09607 3.38548 3.23688V3.50071H5.01611V5.25979H3.40324V12.7536H1.31165V5.25979H0V3.50071H1.31165V3.18409C1.31165 1.19631 2.21563 1.17419e-05 4.3957 0Z"
                fill="#111110"
              />
              <path d="M8.75558 12.753H6.62857V0.0521834H8.75558V12.753Z" fill="#111110" />
              <path
                d="M55.6899 2.9019C55.6899 4.44994 55.6545 5.41747 55.6367 6.78957L55.6545 6.80723C56.3103 6.10362 56.9129 5.57584 57.5864 4.88979L58.9513 3.5001H61.8405L56.8952 7.86264L62 12.753H59.0045C57.8347 11.592 56.7002 10.3783 55.6722 9.23486H55.6545C55.6722 10.0088 55.6899 10.6421 55.6899 11.5216V12.753H53.6162V0.0521834H55.6899V2.9019Z"
                fill="#111110"
              />
            </svg>
          )}
        </div>
      </div>
      <nav css={navStyles}>
        <span css={navItemStyles}>Emails</span>
        <span css={navItemStyles}>Forms</span>
        <span css={navItemStyles}>Workflows</span>
        <span css={navItemStyles}>Checkouts</span>
        <span css={[navItemStyles, navItemActiveStyles]}>Audience</span>
        <span css={navItemStyles}>Analytics</span>
      </nav>
      <div css={accountStyles}>
        <div css={avatarStyles} />
        <div css={chevronStyles}>
          <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
            <path
              d="M1 1L5 5L9 1"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>
    </header>
  );
}
